import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, format } from "date-fns";

// ── Worker identity ──────────────────────────────
export const useMyWorkerRecord = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_worker_record", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tm_workers")
        .select(`
          *,
          rate_schedule:tm_rate_schedules(*),
          personnel_project:personnel_projects(*)
        `)
        .eq("portal_user_id", user!.id)
        .eq("is_active", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
};

// ── Timesheet lines for a worker ─────────────────
export const useMyTimesheetLines = (workerId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_timesheet_lines", workerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tm_timesheet_lines")
        .select(`
          *,
          timesheet:tm_timesheets(
            id, week_ending, status, personnel_project_id,
            personnel_project:personnel_projects(name, project_number)
          ),
          rate_schedule:tm_rate_schedules(role_name, bill_rate_regular, bill_rate_ot)
        `)
        .eq("worker_id", workerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!workerId,
  });
};

// ── Computed stats from timesheet lines ───────────
export const useMyStats = (workerId?: string) => {
  const { data: lines = [] } = useMyTimesheetLines(workerId);

  const now = new Date();
  const weekStart = format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd");
  const weekEnd = format(endOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd");
  const monthStart = format(startOfMonth(now), "yyyy-MM-dd");
  const monthEnd = format(endOfMonth(now), "yyyy-MM-dd");
  const yearStart = format(startOfYear(now), "yyyy-MM-dd");

  const inRange = (we: string | undefined, start: string, end: string) =>
    we != null && we >= start && we <= end;

  const weekLines = lines.filter((l: any) => inRange(l.timesheet?.week_ending, weekStart, weekEnd));
  const monthLines = lines.filter((l: any) => inRange(l.timesheet?.week_ending, monthStart, monthEnd));
  const ytdLines = lines.filter((l: any) => inRange(l.timesheet?.week_ending, yearStart, "9999-12-31"));

  const sum = (arr: any[], key: string) => arr.reduce((s, r) => s + (r[key] || 0), 0);

  return {
    weekHours: sum(weekLines, "total_hours"),
    weekEarned: sum(weekLines, "total_billed"),
    monthHours: sum(monthLines, "total_hours"),
    monthEarned: sum(monthLines, "total_billed"),
    ytdHours: sum(ytdLines, "total_hours"),
    ytdEarned: sum(ytdLines, "total_billed"),
  };
};

// ── Timesheets (grouped by week) ─────────────────
export const useMyTimesheets = (workerId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_timesheets", workerId],
    queryFn: async () => {
      // Get all timesheets that have lines for this worker
      const { data: lines, error } = await supabase
        .from("tm_timesheet_lines")
        .select(`
          *,
          timesheet:tm_timesheets(
            id, week_ending, week_number, status, total_billed,
            personnel_project:personnel_projects(name, project_number)
          )
        `)
        .eq("worker_id", workerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return lines ?? [];
    },
    enabled: !!user && !!workerId,
  });
};

// ── Reimbursements ───────────────────────────────
export const useMyReimbursements = (workerId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_reimbursements", workerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("personnel_reimbursements")
        .select("*")
        .eq("worker_id", workerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!workerId,
  });
};

export const useSubmitReimbursement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, any>) => {
      const { data, error } = await supabase
        .from("personnel_reimbursements")
        .insert(input as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["my_reimbursements", vars.worker_id] });
    },
  });
};

// ── Messages ─────────────────────────────────────
export const useMyMessages = (workerId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_messages", workerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("personnel_messages")
        .select("*")
        .eq("worker_id", workerId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!workerId,
  });
};

export const useSendMessage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, any>) => {
      const { data, error } = await supabase
        .from("personnel_messages")
        .insert(input as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["my_messages", vars.worker_id] });
    },
  });
};

// ── Documents / compliance ───────────────────────
export const useMyDocuments = (vendorId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_documents", vendorId],
    queryFn: async () => {
      const [agreements, i9, everify] = await Promise.all([
        supabase
          .from("contractor_agreements")
          .select("*")
          .eq("vendor_id", vendorId!)
          .order("created_at", { ascending: false }),
        (supabase.from("worker_i9_records") as any)
          .select("*")
          .eq("vendor_id", vendorId!)
          .maybeSingle(),
        (supabase.from("worker_everify_cases") as any)
          .select("*")
          .eq("vendor_id", vendorId!)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      return {
        agreements: agreements.data ?? [],
        i9: i9.data ?? null,
        everify: everify.data ?? null,
      };
    },
    enabled: !!user && !!vendorId,
  });
};

// ── Certifications ───────────────────────────────
export const useMyCertifications = (workerId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_certifications", workerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("personnel_certifications")
        .select("*")
        .eq("worker_id", workerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!workerId,
  });
};

// ── Worker profile update ────────────────────────
export const useUpdateMyProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fields }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from("tm_workers")
        .update(fields)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my_worker_record"] });
    },
  });
};

// ── Timesheet Submission ─────────────────────────
export interface TimesheetSubmitInput {
  worker_id: string;
  personnel_project_id: string;
  company_id: string;
  week_ending: string; // "yyyy-MM-dd"
  regular_hours: number;
  ot_hours: number;
  rate_schedule_id?: string;
}

export const useSubmitTimesheet = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TimesheetSubmitInput) => {
      const { worker_id, personnel_project_id, company_id, week_ending, regular_hours, ot_hours, rate_schedule_id } = input;
      const total_hours = regular_hours + ot_hours;

      // 1. Create the timesheet record
      const { data: ts, error: tsErr } = await supabase
        .from("tm_timesheets")
        .insert({
          worker_id,
          personnel_project_id,
          company_id,
          week_ending,
          status: "submitted",
        })
        .select("id")
        .single();
      if (tsErr) throw tsErr;

      // 2. Create the timesheet line
      const { error: lineErr } = await supabase
        .from("tm_timesheet_lines")
        .insert({
          timesheet_id: ts.id,
          worker_id,
          company_id,
          regular_hours,
          ot_hours,
          total_hours,
          rate_schedule_id: rate_schedule_id ?? null,
        } as any);
      if (lineErr) throw lineErr;

      return ts;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["my_timesheets", vars.worker_id] });
      qc.invalidateQueries({ queryKey: ["my_timesheet_lines", vars.worker_id] });
    },
  });
};

// ── Bills for worker's vendor ────────────────────
export const useMyBills = (vendorId?: string, projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_bills", vendorId, projectId],
    queryFn: async () => {
      let query = supabase
        .from("bills")
        .select(`
          id, bill_number, bill_date, due_date, status, subtotal,
          retainage_amount, retainage_percent, total, description,
          created_at, work_order_id, vendor_id, project_id,
          projects:projects(name, project_number),
          work_orders:work_orders(number, status, description)
        `)
        .eq("vendor_id", vendorId!)
        .order("created_at", { ascending: false });
      if (projectId) {
        query = query.eq("project_id", projectId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!vendorId,
  });
};

// ── Work orders for worker's vendor ─────────────
export const useMyWorkOrders = (vendorId?: string, projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_work_orders", vendorId, projectId],
    queryFn: async () => {
      let query = supabase
        .from("work_orders")
        .select(`
          id, number, status, amount, description, wo_type, phase, priority,
          due_date, created_at, retainage_pct,
          project_id, contractor_id,
          projects:projects(name, project_number)
        `)
        .eq("contractor_id", vendorId!)
        .order("created_at", { ascending: false });
      if (projectId) {
        query = query.eq("project_id", projectId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!vendorId,
  });
};

// ── Notifications for this user ─────────────────
export const useMyNotifications = (limit = 10) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_portal_notifications", user?.id, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("assigned_to", user!.id)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
};

// ── Vendor tax info (read only for worker) ───────
export const useMyTaxInfo = (vendorId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my_tax_info", vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendor_tax_info")
        .select("*")
        .eq("vendor_id", vendorId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!vendorId,
  });
};
